export const PROVENANCE_VERSION = 1;

export const AI_EXECUTION_TYPES = Object.freeze(['rule', 'mock', 'model']);
export const AI_CORPUS_MODES = Object.freeze([
  'none',
  'seeded_local',
  'manual',
  'live_kb',
  'mixed',
]);
export const CONFIDENCE_SOURCES = Object.freeze([
  'model',
  'rules',
  'heuristic',
  'authored',
  'unknown',
]);
export const CITATION_SCORE_SOURCES = Object.freeze([
  'provider_retrieval',
  'keyword_heuristic',
  'authored',
  'none',
]);
export const FALLBACK_REASON_TEXT = Object.freeze({
  bedrock_kb_id_missing: 'The requested Bedrock knowledge base was not configured.',
  provider_load_failed: 'The requested provider could not be loaded.',
  provider_error: 'The requested provider returned an error.',
  provider_timeout: 'The requested provider timed out.',
  unsupported_provider: 'The requested provider is not supported.',
  unknown: 'The requested provider could not complete the execution.',
});
export const PARTIAL_EXECUTION_REASON_TEXT = Object.freeze({
  retrieval_empty: 'The provider returned no retrieval results.',
  retrieval_failed: 'Retrieval did not complete successfully.',
  model_response_incomplete: 'The model response was incomplete.',
  provider_timeout: 'The provider timed out before all work completed.',
  // Typed-intake commit 4: a caller asked for structured JSON output from a provider
  // that cannot produce it (the deterministic mock). Named explicitly rather than
  // folded into `unknown`, because a consumer must be able to tell "no model could
  // answer this shape" from "something went partly wrong".
  structured_output_unsupported: 'The provider cannot produce structured output.',
  unknown: 'The execution completed only partially.',
});

export const ARTIFACT_SOURCE_MODES = Object.freeze([
  'manual_ui',
  'manual_file_import',
  'generated_output',
  'seed',
  'simulated_connector',
  'mixed',
]);
export const CONTENT_AVAILABILITY_STATES = Object.freeze([
  'metadata_only',
  'inline_content',
  'stored_binary',
  'generated_file',
  'mixed',
]);
export const FRESHNESS_DERIVATIONS = Object.freeze([
  'unknown',
  'created_at',
  'source_event',
  'content_observed_at',
  'mixed',
]);
export const CONFIDENCE_DERIVATIONS = Object.freeze([
  'unknown',
  'authored',
  'rules',
  'heuristic',
  'model',
  'review_event',
  'mixed',
]);

export const CONNECTOR_EXECUTION_MODES = Object.freeze(['simulated', 'live']);
export const CONNECTOR_BUSINESS_OUTCOMES = Object.freeze([
  'completed',
  'failed',
  'partial',
  'not_run',
]);
export const SIMULATION_REASON_TEXT = Object.freeze({
  prototype_adapter: 'The connector uses a prototype adapter.',
  configured_simulation: 'The connector is configured to simulate execution.',
  legacy_no_transport_evidence: 'The legacy result contains no evidence of external transport.',
  test_fixture: 'The execution is a test fixture.',
  unknown: 'The execution was simulated.',
});

const SYNTHETIC_ARTIFACT_URI = /^s3:\/\/dha-nexus-demo\/manual\//i;

function fail(contract, errors) {
  throw new TypeError(`${contract} validation failed: ${errors.join('; ')}`);
}

function isPlainObject(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function nonEmptyString(value) {
  return typeof value === 'string' && value.trim().length > 0;
}

function nullableString(value) {
  return value === null || nonEmptyString(value);
}

function isoDate(value) {
  return nonEmptyString(value) && Number.isFinite(Date.parse(value));
}

function enumValue(value, values) {
  return values.includes(value);
}

function validateVersion(value, errors) {
  if (value !== PROVENANCE_VERSION) errors.push('provenanceVersion must be 1');
}

function validateOccurrenceState(value, name, reasonText, errors) {
  if (!isPlainObject(value)) {
    errors.push(`${name} must be a plain object`);
    return;
  }
  if (typeof value.occurred !== 'boolean') errors.push(`${name}.occurred must be boolean`);
  if (!nullableString(value.reason)) errors.push(`${name}.reason must be a string or null`);
  if (value.occurred === false && value.reason !== null) {
    errors.push(`${name}.reason must be null when no occurrence is reported`);
  }
  if (value.occurred === true && !Object.hasOwn(reasonText, value.reason)) {
    errors.push(`${name}.reason must be a supported machine-readable reason code`);
  }
}

export function validateAiProvenance(provenance) {
  const errors = [];
  if (!isPlainObject(provenance)) fail('AI provenance', ['value must be a plain object']);
  validateVersion(provenance.provenanceVersion, errors);
  if (!enumValue(provenance.executionType, AI_EXECUTION_TYPES)) {
    errors.push('executionType is invalid');
  }
  if (!nonEmptyString(provenance.requestedProvider)) {
    errors.push('requestedProvider must be a non-empty string');
  }
  if (!nonEmptyString(provenance.actualProvider)) {
    errors.push('actualProvider must be a non-empty string');
  }
  if (typeof provenance.modelInvoked !== 'boolean') {
    errors.push('modelInvoked must be boolean');
  }
  if (!nullableString(provenance.modelId)) errors.push('modelId must be a string or null');
  if (provenance.modelInvoked === false && provenance.modelId !== null) {
    errors.push('modelId must be null when modelInvoked is false');
  }
  if (provenance.modelInvoked === true && !nonEmptyString(provenance.modelId)) {
    errors.push('modelId is required when modelInvoked is true');
  }
  if (provenance.executionType === 'model' && provenance.modelInvoked !== true) {
    errors.push('model execution requires modelInvoked true');
  }
  if (['rule', 'mock'].includes(provenance.executionType) && provenance.modelInvoked !== false) {
    errors.push(`${provenance.executionType} execution cannot invoke a model`);
  }
  if (!enumValue(provenance.corpusMode, AI_CORPUS_MODES)) errors.push('corpusMode is invalid');
  if (provenance.executionType === 'mock' && provenance.corpusMode === 'live_kb') {
    errors.push('mock execution cannot use a live knowledge base');
  }
  validateOccurrenceState(provenance.fallback, 'fallback', FALLBACK_REASON_TEXT, errors);
  validateOccurrenceState(
    provenance.partialExecution,
    'partialExecution',
    PARTIAL_EXECUTION_REASON_TEXT,
    errors,
  );
  if (!isPlainObject(provenance.confidence)) {
    errors.push('confidence must be a plain object');
  } else {
    const value = provenance.confidence.value;
    if (value !== null && (!Number.isFinite(value) || value < 0 || value > 1)) {
      errors.push('confidence.value must be null or a finite number from 0 to 1');
    }
    if (!enumValue(provenance.confidence.source, CONFIDENCE_SOURCES)) {
      errors.push('confidence.source is invalid');
    }
  }
  if (!enumValue(provenance.citationScoreSource, CITATION_SCORE_SOURCES)) {
    errors.push('citationScoreSource is invalid');
  }
  if (typeof provenance.seeded !== 'boolean') errors.push('seeded must be boolean');
  if (typeof provenance.illustrative !== 'boolean') errors.push('illustrative must be boolean');
  if (provenance.executionType === 'rule') {
    if (!isPlainObject(provenance.rulesEngine)) {
      errors.push('rulesEngine is required for rule execution');
    } else {
      if (!nonEmptyString(provenance.rulesEngine.id)) errors.push('rulesEngine.id is required');
      if (!nonEmptyString(provenance.rulesEngine.version)) {
        errors.push('rulesEngine.version is required');
      }
    }
  } else if (provenance.rulesEngine !== null) {
    errors.push('rulesEngine must be null unless executionType is rule');
  }
  if (errors.length) fail('AI provenance', errors);
  return provenance;
}

export function validateArtifactProvenance(provenance) {
  const errors = [];
  if (!isPlainObject(provenance)) fail('Artifact provenance', ['value must be a plain object']);
  validateVersion(provenance.provenanceVersion, errors);
  if (!enumValue(provenance.sourceMode, ARTIFACT_SOURCE_MODES)) {
    errors.push('sourceMode is invalid');
  }
  if (!enumValue(provenance.contentAvailability, CONTENT_AVAILABILITY_STATES)) {
    errors.push('contentAvailability is invalid');
  }
  if (typeof provenance.binaryStored !== 'boolean') errors.push('binaryStored must be boolean');
  if (typeof provenance.storageVerified !== 'boolean') {
    errors.push('storageVerified must be boolean');
  }
  if (!nullableString(provenance.storageVerifiedAt)) {
    errors.push('storageVerifiedAt must be a string or null');
  }
  if (!nullableString(provenance.storageBackend)) {
    errors.push('storageBackend must be a string or null');
  }
  if (!nullableString(provenance.objectIdentity)) {
    errors.push('objectIdentity must be a string or null');
  }
  if (
    provenance.byteLength !== null &&
    (!Number.isSafeInteger(provenance.byteLength) || provenance.byteLength < 0)
  ) {
    errors.push('byteLength must be null or a non-negative safe integer');
  }
  if (!nullableString(provenance.contentHash)) errors.push('contentHash must be a string or null');
  if (provenance.binaryStored === false) {
    if (provenance.storageVerified !== false) {
      errors.push('storage cannot be verified when binaryStored is false');
    }
    for (const field of [
      'storageVerifiedAt',
      'storageBackend',
      'objectIdentity',
      'byteLength',
      'contentHash',
    ]) {
      if (provenance[field] !== null)
        errors.push(`${field} must be null when binaryStored is false`);
    }
  }
  if (provenance.storageVerified === true) {
    if (provenance.binaryStored !== true)
      errors.push('verified storage requires stored binary content');
    if (!isoDate(provenance.storageVerifiedAt)) {
      errors.push('storageVerifiedAt must be an ISO date when storage is verified');
    }
    if (!nonEmptyString(provenance.storageBackend)) {
      errors.push('storageBackend is required when storage is verified');
    }
    if (!nonEmptyString(provenance.objectIdentity)) {
      errors.push('objectIdentity is required when storage is verified');
    }
  } else if (provenance.storageVerifiedAt !== null) {
    errors.push('storageVerifiedAt must be null when storage is not verified');
  }
  if (provenance.contentAvailability === 'metadata_only' && provenance.binaryStored !== false) {
    errors.push('metadata_only content cannot report a stored binary');
  }
  if (provenance.contentAvailability === 'stored_binary' && provenance.binaryStored !== true) {
    errors.push('stored_binary content requires binaryStored true');
  }
  const review = provenance.reviewState;
  if (!isPlainObject(review)) {
    errors.push('reviewState must be a plain object');
  } else if (typeof review.reviewed !== 'boolean') {
    errors.push('reviewState.reviewed must be boolean');
  } else if (review.reviewed) {
    if (!nonEmptyString(review.reviewEventId)) {
      errors.push('reviewState.reviewEventId is required when reviewed');
    }
    if (!nonEmptyString(review.reviewedBy)) {
      errors.push('reviewState.reviewedBy is required when reviewed');
    }
    if (!isoDate(review.reviewedAt)) {
      errors.push('reviewState.reviewedAt must be an ISO date when reviewed');
    }
  } else {
    for (const field of ['reviewEventId', 'reviewedBy', 'reviewedAt']) {
      if (review[field] !== null)
        errors.push(`reviewState.${field} must be null when not reviewed`);
    }
  }
  if (!enumValue(provenance.freshnessDerivation, FRESHNESS_DERIVATIONS)) {
    errors.push('freshnessDerivation is invalid');
  }
  if (!enumValue(provenance.confidenceDerivation, CONFIDENCE_DERIVATIONS)) {
    errors.push('confidenceDerivation is invalid');
  }
  if (errors.length) fail('Artifact provenance', errors);
  return provenance;
}

export function validateConnectorExecution(provenance) {
  const errors = [];
  if (!isPlainObject(provenance)) {
    fail('Connector execution provenance', ['value must be a plain object']);
  }
  validateVersion(provenance.provenanceVersion, errors);
  if (!enumValue(provenance.executionMode, CONNECTOR_EXECUTION_MODES)) {
    errors.push('executionMode is invalid');
  }
  if (typeof provenance.externalCallMade !== 'boolean') {
    errors.push('externalCallMade must be boolean');
  }
  for (const field of ['adapterId', 'maturityLevel', 'environmentMode']) {
    if (!nonEmptyString(provenance[field])) errors.push(`${field} must be a non-empty string`);
  }
  if (!nullableString(provenance.simulationReason)) {
    errors.push('simulationReason must be a string or null');
  }
  if (!enumValue(provenance.businessOutcome, CONNECTOR_BUSINESS_OUTCOMES)) {
    errors.push('businessOutcome is invalid');
  }
  if (provenance.executionMode === 'live') {
    if (provenance.externalCallMade !== true)
      errors.push('live execution requires an external call');
    if (provenance.simulationReason !== null) {
      errors.push('simulationReason must be null for live execution');
    }
  }
  if (provenance.executionMode === 'simulated') {
    if (provenance.externalCallMade !== false) {
      errors.push('simulated execution cannot report an external call');
    }
    if (!Object.hasOwn(SIMULATION_REASON_TEXT, provenance.simulationReason)) {
      errors.push('simulationReason must be a supported machine-readable reason code');
    }
  }
  if (errors.length) fail('Connector execution provenance', errors);
  return provenance;
}

function firstNonEmpty(...values) {
  return values.find(nonEmptyString) || null;
}

function knownReason(value, reasonText) {
  return Object.hasOwn(reasonText, value) ? value : 'unknown';
}

function legacyCorpusMode(raw, executionType) {
  const value = raw.corpusMode;
  const mapped = {
    'local-seeded-prime': 'seeded_local',
    'seeded-local': 'seeded_local',
    local: 'seeded_local',
    seeded: 'seeded_local',
    none: 'none',
    manual: 'manual',
    live_kb: 'live_kb',
    mixed: 'mixed',
  }[value];
  if (mapped && !(executionType === 'mock' && mapped === 'live_kb')) return mapped;
  return executionType === 'mock' ? 'seeded_local' : 'none';
}

export function normalizeLegacyAiRun(input) {
  const raw = input?.provenance || input || {};
  if (raw.provenanceVersion === PROVENANCE_VERSION) return validateAiProvenance(raw);
  const explicitRule = raw.executionType === 'rule' || raw.rulesEngine || raw.rulesVersion;
  const provenModelInvocation = raw.modelInvoked === true && nonEmptyString(raw.modelId);
  const executionType = explicitRule ? 'rule' : provenModelInvocation ? 'model' : 'mock';
  const actualProvider = firstNonEmpty(raw.actualProvider, raw.provider, raw.mode, 'unknown');
  const requestedProvider = firstNonEmpty(raw.requestedProvider, actualProvider, 'unknown');
  const fallbackOccurred = raw.fallback?.occurred === true || raw.fallbackOccurred === true;
  const fallbackReason = raw.fallback?.reason || raw.fallbackReason;
  const partialOccurred =
    raw.partialExecution?.occurred === true || raw.partialExecutionOccurred === true;
  const partialReason = raw.partialExecution?.reason || raw.partialExecutionReason;
  const mockLike = executionType === 'mock';
  const corpusMode = legacyCorpusMode(raw, executionType);
  const citationScoreSource = enumValue(raw.citationScoreSource, CITATION_SCORE_SOURCES)
    ? raw.citationScoreSource
    : mockLike || corpusMode === 'seeded_local'
      ? 'keyword_heuristic'
      : 'none';
  const rulesEngine =
    executionType === 'rule'
      ? {
          id: firstNonEmpty(raw.rulesEngine?.id, raw.rulesEngineId, raw.kind, 'legacy-rules'),
          version: firstNonEmpty(
            raw.rulesEngine?.version,
            raw.rulesVersion,
            raw.version,
            'legacy-unknown',
          ),
        }
      : null;
  const provenance = {
    provenanceVersion: PROVENANCE_VERSION,
    executionType,
    requestedProvider,
    actualProvider,
    modelInvoked: provenModelInvocation,
    modelId: provenModelInvocation ? raw.modelId : null,
    corpusMode,
    fallback: {
      occurred: fallbackOccurred,
      reason: fallbackOccurred ? knownReason(fallbackReason, FALLBACK_REASON_TEXT) : null,
    },
    partialExecution: {
      occurred: partialOccurred,
      reason: partialOccurred ? knownReason(partialReason, PARTIAL_EXECUTION_REASON_TEXT) : null,
    },
    confidence: {
      value: Number.isFinite(raw.confidence?.value)
        ? raw.confidence.value
        : Number.isFinite(raw.confidence)
          ? raw.confidence
          : null,
      source: enumValue(raw.confidence?.source || raw.confidenceSource, CONFIDENCE_SOURCES)
        ? raw.confidence?.source || raw.confidenceSource
        : 'unknown',
    },
    citationScoreSource,
    seeded:
      typeof raw.seeded === 'boolean' ? raw.seeded : mockLike || corpusMode === 'seeded_local',
    illustrative:
      typeof raw.illustrative === 'boolean'
        ? raw.illustrative
        : mockLike || corpusMode === 'seeded_local',
    rulesEngine,
  };
  return validateAiProvenance(provenance);
}

function legacySourceMode(raw) {
  if (enumValue(raw.sourceMode, ARTIFACT_SOURCE_MODES)) return raw.sourceMode;
  const source = String(raw.sourceSystem || raw.source || '').toLowerCase();
  if (source.includes('seed')) return 'seed';
  if (source.includes('connector')) return 'simulated_connector';
  if (source.includes('generated')) return 'generated_output';
  return 'manual_ui';
}

export function normalizeLegacyArtifact(input) {
  const raw = input?.provenance || input || {};
  if (raw.provenanceVersion === PROVENANCE_VERSION) return validateArtifactProvenance(raw);
  const syntheticUri = SYNTHETIC_ARTIFACT_URI.test(String(raw.storageUri || raw.storageRef || ''));
  const binaryStored = raw.binaryStored === true && !syntheticUri;
  const storageVerified =
    binaryStored === true &&
    raw.storageVerified === true &&
    isoDate(raw.storageVerifiedAt) &&
    nonEmptyString(raw.storageBackend) &&
    nonEmptyString(raw.objectIdentity || raw.storageUri || raw.storageRef);
  const reviewed =
    raw.reviewState?.reviewed === true &&
    nonEmptyString(raw.reviewState.reviewEventId) &&
    nonEmptyString(raw.reviewState.reviewedBy) &&
    isoDate(raw.reviewState.reviewedAt);
  const contentAvailability = enumValue(raw.contentAvailability, CONTENT_AVAILABILITY_STATES)
    ? raw.contentAvailability
    : binaryStored
      ? 'stored_binary'
      : nonEmptyString(raw.content)
        ? 'inline_content'
        : 'metadata_only';
  const provenance = {
    provenanceVersion: PROVENANCE_VERSION,
    sourceMode: legacySourceMode(raw),
    contentAvailability,
    binaryStored,
    storageVerified,
    storageVerifiedAt: storageVerified ? raw.storageVerifiedAt : null,
    storageBackend: storageVerified ? raw.storageBackend : null,
    objectIdentity: storageVerified
      ? firstNonEmpty(raw.objectIdentity, raw.storageUri, raw.storageRef)
      : null,
    byteLength:
      binaryStored && Number.isSafeInteger(raw.byteLength) && raw.byteLength >= 0
        ? raw.byteLength
        : null,
    contentHash: binaryStored ? firstNonEmpty(raw.contentHash) : null,
    reviewState: reviewed
      ? {
          reviewed: true,
          reviewEventId: raw.reviewState.reviewEventId,
          reviewedBy: raw.reviewState.reviewedBy,
          reviewedAt: raw.reviewState.reviewedAt,
        }
      : { reviewed: false, reviewEventId: null, reviewedBy: null, reviewedAt: null },
    freshnessDerivation: enumValue(raw.freshnessDerivation, FRESHNESS_DERIVATIONS)
      ? raw.freshnessDerivation
      : 'unknown',
    confidenceDerivation: enumValue(raw.confidenceDerivation, CONFIDENCE_DERIVATIONS)
      ? raw.confidenceDerivation
      : 'unknown',
  };
  return validateArtifactProvenance(provenance);
}

function legacyBusinessOutcome(raw) {
  if (enumValue(raw.businessOutcome, CONNECTOR_BUSINESS_OUTCOMES)) return raw.businessOutcome;
  const status = String(raw.status || '').toLowerCase();
  if (['passed', 'succeeded', 'success', 'completed'].includes(status)) return 'completed';
  if (['failed', 'error'].includes(status)) return 'failed';
  if (['partial', 'completed_with_errors'].includes(status)) return 'partial';
  return 'not_run';
}

export function normalizeLegacyConnectorResult(input) {
  const raw = input?.provenance || input || {};
  if (raw.provenanceVersion === PROVENANCE_VERSION) return validateConnectorExecution(raw);
  const explicitExecution =
    enumValue(raw.executionMode, CONNECTOR_EXECUTION_MODES) &&
    typeof raw.externalCallMade === 'boolean';
  const executionMode = explicitExecution ? raw.executionMode : 'simulated';
  const externalCallMade = explicitExecution ? raw.externalCallMade : false;
  const simulationReason =
    executionMode === 'simulated'
      ? knownReason(raw.simulationReason || 'legacy_no_transport_evidence', SIMULATION_REASON_TEXT)
      : null;
  const provenance = {
    provenanceVersion: PROVENANCE_VERSION,
    executionMode,
    externalCallMade,
    adapterId: firstNonEmpty(raw.adapterId, raw.connectorId, raw.id, 'legacy-unknown-adapter'),
    maturityLevel: firstNonEmpty(raw.maturityLevel, 'legacy-unknown'),
    environmentMode: firstNonEmpty(raw.environmentMode, 'legacy-unknown'),
    simulationReason,
    businessOutcome: legacyBusinessOutcome(raw),
  };
  return validateConnectorExecution(provenance);
}

function aiBadge(provenance) {
  validateAiProvenance(provenance);
  let badge;
  if (provenance.executionType === 'rule') {
    badge = {
      label: `Deterministic rules (v${provenance.rulesEngine.version})`,
      tooltip: `No model was invoked. Output was produced by the ${provenance.rulesEngine.id} rules engine version ${provenance.rulesEngine.version}.`,
    };
  } else if (provenance.executionType === 'mock') {
    badge = {
      label: 'Simulated assistant output',
      tooltip: 'No model was invoked. This output was produced by the simulated assistant path.',
    };
  } else {
    const corpusCopy = {
      none: [
        'Model-generated · no retrieval corpus',
        'A model was invoked without a retrieval corpus.',
      ],
      seeded_local: [
        'Model-generated · illustrative local corpus',
        'A model was invoked using seeded local content. Treat the output as illustrative.',
      ],
      manual: [
        'Model-generated · manually supplied corpus',
        'A model was invoked using manually supplied content.',
      ],
      live_kb: [
        'Model-generated · live knowledge base',
        'A model was invoked using provider retrieval from the live knowledge base.',
      ],
      mixed: [
        'Model-generated · mixed corpus',
        'A model was invoked using content from more than one corpus mode.',
      ],
    }[provenance.corpusMode];
    badge = { label: corpusCopy[0], tooltip: corpusCopy[1] };
  }
  const qualifiers = [];
  const details = [];
  if (provenance.fallback.occurred) {
    qualifiers.push('fallback');
    details.push(FALLBACK_REASON_TEXT[provenance.fallback.reason]);
  }
  if (provenance.partialExecution.occurred) {
    qualifiers.push('partial');
    details.push(PARTIAL_EXECUTION_REASON_TEXT[provenance.partialExecution.reason]);
  }
  return qualifiers.length
    ? {
        label: `${badge.label} · ${qualifiers.join(' · ')}`,
        tooltip: `${badge.tooltip} ${details.join(' ')}`,
      }
    : badge;
}

function artifactBadge(provenance) {
  validateArtifactProvenance(provenance);
  let badge;
  if (!provenance.binaryStored) {
    badge = {
      label: 'Metadata only - no file stored',
      tooltip: 'Artifact metadata exists, but no binary file is stored or verified.',
    };
  } else if (!provenance.storageVerified) {
    badge = {
      label: 'File stored · verification pending',
      tooltip:
        'Binary content is recorded as stored, but its storage identity has not been verified.',
    };
  } else {
    badge = {
      label: 'Stored file · verified',
      tooltip: `Binary content was verified in ${provenance.storageBackend}.`,
    };
  }
  if (provenance.reviewState.reviewed) {
    return {
      label: `${badge.label} · reviewed`,
      tooltip: `${badge.tooltip} Review event ${provenance.reviewState.reviewEventId} is recorded.`,
    };
  }
  return badge;
}

function connectorBadge(provenance) {
  validateConnectorExecution(provenance);
  if (provenance.executionMode === 'simulated') {
    return {
      label: 'Simulated sync - no external systems contacted',
      tooltip: `The workflow ${
        provenance.businessOutcome === 'completed'
          ? 'completed'
          : `ended as ${provenance.businessOutcome}`
      } in simulation mode. No external call was made.`,
    };
  }
  return {
    label: 'Live connector execution · external call made',
    tooltip: `The ${provenance.adapterId} adapter made an external call. Business outcome: ${provenance.businessOutcome}.`,
  };
}

export function badgeFor(provenance) {
  if (provenance && Object.hasOwn(provenance, 'executionType')) return aiBadge(provenance);
  if (provenance && Object.hasOwn(provenance, 'sourceMode')) return artifactBadge(provenance);
  if (provenance && Object.hasOwn(provenance, 'executionMode')) return connectorBadge(provenance);
  throw new TypeError('Unsupported provenance contract');
}
